import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Plus, Users } from 'lucide-react'
import { DashedButton } from './DashedButton'
import { ThumbStack } from './profile/ThumbStack'
import { memberCountLabel } from '../lib/commonStrings'
import { cn } from '../lib/utils'
import { canLinkDirectly } from '@shared/groupRoles'
import type { Group } from '../types'

/**
 * The "which group are you going with" list — one row per group that isn't
 * linked to the event yet, ending in a create-a-new-group row. Lives in the
 * link-a-group picker (post-RSVP prompt and the event Groups section's +).
 */
export function LinkGroupList({
  groups,
  onLink,
  onCreateGroup,
  viewerId,
}: {
  groups: Group[]
  /** Rows for groups the viewer can only suggest to (approval required, not an admin) read "Suggest". */
  viewerId?: string | null
  onLink: (groupId: string) => void
  onCreateGroup: () => void
}) {
  return (
    <>
      {groups.map(group => (
        <PickGroupRow
          key={group.id}
          group={group}
          onClick={() => onLink(group.id)}
          trailing={
            <span className="shrink-0 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {viewerId && !canLinkDirectly(group, viewerId)
                ? fbt('Suggest', 'Pill label on a group row that suggests this event to the group’s admins when tapped')
                : fbt('Link', 'Pill label on a group row that links the group to this event when tapped')}
            </span>
          }
        />
      ))}
      <DashedRow
        onClick={onCreateGroup}
        label={fbt(
          'Create a new group',
          'Last row of the link-a-group list that opens the create-group dialog',
        )}
      />
    </>
  )
}

/** A compact group row for pickers: square cover, name, member count, and a trailing pill. */
export function PickGroupRow({
  group,
  onClick,
  trailing,
}: {
  group: Group
  onClick: () => void
  trailing: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-muted"
    >
      <ThumbStack srcs={[group.imageUrl]} icon={Users} size="md" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{group.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {memberCountLabel(group.memberIds.length)}
        </span>
      </span>
      {trailing}
    </button>
  )
}

/** `className` lets a dense context (a dialog's list) match its neighbours' height. */
export function DashedRow({ onClick, label, className }: { onClick: () => void; label: ReactNode; className?: string }) {
  return (
    <DashedButton
      onClick={onClick}
      className={cn('flex items-center justify-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground', className)}
    >
      <Plus className="size-4" />
      <span>{label}</span>
    </DashedButton>
  )
}
